import Phaser from 'phaser';

export type MoleculeType = 'simple' | 'rna' | 'protein' | 'enzyme';

export class Molecule extends Phaser.GameObjects.Container {
    private moleculeType: MoleculeType;
    private shape: Phaser.GameObjects.Shape | Phaser.GameObjects.Graphics;
    private isPositioned: boolean = false;
    
    private static tooltips: Record<MoleculeType, string> = {
        'simple': 'Simple Molecule: Basic building block for early chemical reactions',
        'rna': 'RNA-like Molecule: Can store simple information',
        'protein': 'Protein-like Structure: Helps with molecular interactions',
        'enzyme': 'Catalytic Molecule: Speeds up chemical reactions'
    };
    
    constructor(scene: Phaser.Scene, x: number, y: number, color: number, type: MoleculeType = 'simple') {
        super(scene, x, y);
        
        this.moleculeType = type;
        
        switch (type) {
            case 'simple':
                this.shape = scene.add.circle(0, 0, 10, color);
                break;
                
            case 'rna':
                // Create an RNA-like molecule (multiple connected circles)
                const rnaGraphics = scene.add.graphics();
                rnaGraphics.fillStyle(color, 1);
                
                // Draw pattern of small circles
                for (let i = 0; i < 4; i++) {
                    const angle = (i / 4) * Math.PI * 2;
                    const x = Math.cos(angle) * 6;
                    const y = Math.sin(angle) * 6;
                    rnaGraphics.fillCircle(x, y, 3);
                }
                rnaGraphics.fillCircle(0, 0, 4); // Center circle
                this.shape = rnaGraphics;
                break;
                
            case 'protein':
                // Create protein-like folded structure
                const proteinGraphics = scene.add.graphics();
                proteinGraphics.fillStyle(color, 1);
                proteinGraphics.fillRect(-8, -4, 16, 8);
                proteinGraphics.fillRect(-4, -8, 8, 16);
                this.shape = proteinGraphics;
                break;
                
            case 'enzyme':
                // Create hexagonal enzyme-like shape
                const enzymeGraphics = scene.add.graphics();
                enzymeGraphics.fillStyle(color, 1);
                
                // Draw hexagon
                const hexPoints = [];
                for (let i = 0; i < 6; i++) {
                    const angle = (i / 6) * Math.PI * 2;
                    const x = Math.cos(angle) * 10;
                    const y = Math.sin(angle) * 10;
                    hexPoints.push(new Phaser.Math.Vector2(x, y));
                }
                
                enzymeGraphics.fillPoints(hexPoints, true);
                this.shape = enzymeGraphics;
                break;
        }
        
        // Add shape to container
        this.add(this.shape);
        
        // Set size for collision detection
        this.setSize(40, 40);
        
        // Add to scene FIRST before making interactive
        scene.add.existing(this);
        
        // Use the most basic setInteractive() call without custom hitArea
        this.setInteractive();
        
        // Set as draggable
        scene.input.setDraggable(this);
        
        // Set data attributes
        this.setData('type', 'molecule');
        this.setData('moleculeType', type);
        this.setData('tooltip', Molecule.tooltips[type]);
        
        // Set depth to ensure it's above the background but below UI
        this.setDepth(10);
        
        // Log creation
        console.log(`Created molecule (${type}) at ${x},${y}, interactive: ${!!this.input?.enabled}`);
    }
    
    public getTooltipText(): string {
        return Molecule.tooltips[this.moleculeType];
    }
    
    public getMoleculeType(): MoleculeType {
        return this.moleculeType;
    }
    
    public setPositioned(positioned: boolean): void {
        this.setData('positioned', positioned);
    }
    
    public isCorrectlyPositioned(): boolean {
        return this.getData('positioned') === true;
    }
    
    // Apply gentle floating animation when in water
    public applyFloatingEffect(): void {
        if (this.isPositioned) return;
        
        const scene = this.scene;
        const startX = this.x;
        const startY = this.y;
        const amplitude = 5 + Math.random() * 3; // Random floating amplitude
        const period = 3000 + Math.random() * 2000; // Random period
        const phase = Math.random() * Math.PI * 2; // Random starting phase
        
        scene.tweens.add({
            targets: this,
            x: {
                getEnd: () => startX + Math.sin(phase + scene.time.now / period * Math.PI * 2) * amplitude,
                getStart: () => this.x
            },
            y: {
                getEnd: () => startY + Math.cos(phase + 1 + scene.time.now / period * Math.PI * 2) * amplitude,
                getStart: () => this.y
            },
            duration: 100,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
    }
} 